/**
 * Device-local registry linking files on THIS machine to their cloud rows —
 * the memory behind "don't create a second copy of a file I already opened".
 *
 * Deliberately client-side (IndexedDB), NOT server metadata:
 *
 *   - local paths and machine ids never leave the device, so nothing has to
 *     strip them from share / publish / export surfaces;
 *   - the link is per-device by nature (an inode means nothing on another
 *     machine), so device storage is the correct scope;
 *   - losing the store (cleared site data) degrades gracefully: exact
 *     duplicates are still caught server-side by the CAS hash lookup.
 *
 * Keys are `${userId}|${originId}`. `originId` is a stable per-device file
 * identity: on the desktop app a volume+inode pair (survives rename/move
 * within a volume), in the browser a generated id whose record carries the
 * `FileSystemFileHandle` — matched by `isSameEntry`, since the web platform
 * exposes no paths.
 *
 * Every method degrades to a no-op result when IndexedDB is unavailable or
 * broken (private mode, storage eviction) — the open flow then behaves as if
 * no link existed, which is safe.
 */

export interface LocalFileLink {
  /** `${userId}|${originId}` — the primary key. */
  key: string;
  userId: string;
  originId: string;
  contentId: string;
  /** Hash disk and cloud agreed on at the last reconcile. */
  lastSyncHash: string;
  /** Last seen file name, for dialog copy. */
  name: string;
  /** Display-only hint (desktop app); never sent to the server. */
  path?: string;
  /** Browser origin identity (PWA); structured-cloneable into IDB. */
  handle?: FileSystemFileHandle;
  updatedAt: number;
}

const DB_NAME = 'robutler-open-links';
const STORE = 'links';

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, 1);
    } catch {
      return resolve(null);
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('byUser', 'userId');
        store.createIndex('byContent', ['userId', 'contentId']);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T | null> {
  return new Promise((resolve) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

export function linkKey(userId: string, originId: string): string {
  return `${userId}|${originId}`;
}

export async function getLinkByOrigin(
  userId: string,
  originId: string,
): Promise<LocalFileLink | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const tx = db.transaction(STORE, 'readonly');
    const row = await requestToPromise(tx.objectStore(STORE).get(linkKey(userId, originId)));
    return (row as LocalFileLink | undefined) ?? null;
  } catch {
    return null;
  } finally {
    db.close();
  }
}

/**
 * PWA lookup: no path exists, so walk the user's stored handles and ask the
 * platform whether any denotes the same on-disk entry as `handle`.
 */
export async function findLinkByHandle(
  userId: string,
  handle: FileSystemFileHandle,
): Promise<LocalFileLink | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const tx = db.transaction(STORE, 'readonly');
    const rows = await requestToPromise(
      tx.objectStore(STORE).index('byUser').getAll(IDBKeyRange.only(userId)),
    );
    for (const row of (rows as LocalFileLink[] | null) ?? []) {
      if (!row.handle) continue;
      try {
        if (await handle.isSameEntry(row.handle)) return row;
      } catch {
        // A handle can go stale (file deleted, permission revoked); skip it.
      }
    }
    return null;
  } catch {
    return null;
  } finally {
    db.close();
  }
}

export async function putLink(link: LocalFileLink): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(link);
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    // Storage unavailable — the link just isn't remembered.
  } finally {
    db.close();
  }
}

export async function deleteLink(key: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    // ignore
  } finally {
    db.close();
  }
}
